import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Skeleton,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  RichTextEditor,
  RichTextRenderer,
  useToast,
} from '@org/ui';
import type { RichTextDoc } from '@org/model';
import { extractPlainText } from '@org/utils-client';
import { ArrowLeft, Heart, MessageCircle, Pin, Lock, Send } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { Poll } from '@/components/poll';
import { ShareMenu } from '@/components/share-menu';
import { LikersStack } from '@/components/likers-stack';
import { renderMentionLink } from '@/components/mention-link';
import { useMentionSearch } from '@/hooks/use-mention-search';
import { useRichTextMediaUpload } from '@/hooks/use-rich-text-media-upload';
import { EMPTY_DOC, isDocEmpty } from '@/utils/rich-text-empty';

export const Route = createFileRoute('/posts/$postId')({
  component: PostDetailPage,
});

function PostDetailPage() {
  const { postId } = Route.useParams();
  const { t } = useTranslation('community');
  const { toast } = useToast();

  const [commentDoc, setCommentDoc] = useState<RichTextDoc>(EMPTY_DOC);
  const [isLiked, setIsLiked] = useState(false);

  const searchUsers = useMentionSearch();
  const uploadMedia = useRichTextMediaUpload();

  const { data: post, isLoading } = communityTrpc.posts.get.useQuery({ postId });
  const { data: commentsData } = communityTrpc.comments.listByPost.useQuery({
    postId,
    limit: 50,
  });
  const { data: likedData } = communityTrpc.likes.hasLiked.useQuery({
    targetType: 'post',
    targetId: postId,
  });

  useEffect(() => {
    if (likedData) setIsLiked(likedData.hasLiked);
  }, [likedData?.hasLiked]);

  const comments = commentsData?.comments ?? [];
  const utils = communityTrpc.useUtils();

  const commentLikeTargets = useMemo(
    () => (commentsData?.comments ?? []).map((c) => ({
      targetType: 'comment' as const,
      targetId: c.commentId,
    })),
    [commentsData?.comments],
  );

  const { data: commentLikedData } = communityTrpc.likes.hasLikedMany.useQuery(
    { targets: commentLikeTargets },
    { enabled: commentLikeTargets.length > 0 },
  );

  const [commentLikedMap, setCommentLikedMap] = useState<Record<string, boolean>>({});
  const [commentLikeCountOverride, setCommentLikeCountOverride] = useState<Record<string, number>>({});

  const commentBaseCountMap = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of commentsData?.comments ?? []) {
      map[c.commentId] = c.likesCount;
    }
    return map;
  }, [commentsData?.comments]);

  useEffect(() => {
    if (commentLikedData?.likes) {
      const next: Record<string, boolean> = {};
      for (const [key, liked] of Object.entries(commentLikedData.likes)) {
        const [, id] = key.split(':');
        next[id] = liked;
      }
      setCommentLikedMap((prev) => ({ ...prev, ...next }));
    }
  }, [commentLikedData]);

  const likeMutation = communityTrpc.likes.toggle.useMutation({
    onMutate: () => setIsLiked((v) => !v),
    onError: () => setIsLiked((v) => !v),
    onSettled: () => {
      utils.posts.get.invalidate({ postId });
      utils.likes.hasLiked.invalidate({ targetType: 'post', targetId: postId });
    },
  });

  const commentLikeMutation = communityTrpc.likes.toggle.useMutation({
    onMutate: ({ targetId }) => {
      const wasLiked = commentLikedMap[targetId];
      setCommentLikedMap((m) => ({ ...m, [targetId]: !wasLiked }));
      setCommentLikeCountOverride((m) => ({
        ...m,
        [targetId]: (m[targetId] ?? commentBaseCountMap[targetId] ?? 0) + (wasLiked ? -1 : 1),
      }));
    },
    onError: (_e, { targetId }) => {
      const wasLiked = commentLikedMap[targetId];
      setCommentLikedMap((m) => ({ ...m, [targetId]: !wasLiked }));
      setCommentLikeCountOverride((m) => ({
        ...m,
        [targetId]: (m[targetId] ?? commentBaseCountMap[targetId] ?? 0) + (wasLiked ? 1 : -1),
      }));
    },
    onSettled: (_data, _err, { targetId }) => {
      setCommentLikeCountOverride((m) => {
        const { [targetId]: _, ...rest } = m;
        return rest;
      });
      utils.likes.hasLikedMany.invalidate();
      utils.comments.listByPost.invalidate({ postId });
    },
  });

  const createComment = communityTrpc.comments.create.useMutation({
    onSuccess: () => {
      utils.comments.listByPost.invalidate({ postId });
      utils.posts.get.invalidate({ postId });
    },
    onError: (error) => {
      toast({ title: t('common.error'), description: error.message, variant: 'destructive' });
    },
  });

  const handleSubmitComment = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (isDocEmpty(commentDoc)) return;
    createComment.mutate(
      { postId, content: commentDoc },
      { onSuccess: () => setCommentDoc(EMPTY_DOC) },
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="flex items-start gap-3">
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="space-y-6">
        <Link to="/">
          <Button variant="ghost" size="sm" className="gap-2">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t('postDetail.backToFeed')}
          </Button>
        </Link>
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <p className="text-muted-foreground">{t('postDetail.notFound')}</p>
        </div>
      </div>
    );
  }

  const authorName = post.authorDisplayName ||
    [post.authorFirstName, post.authorLastName].filter(Boolean).join(' ') ||
    'Anonymous';
  const initial = authorName.slice(0, 1).toUpperCase();

  return (
    <div className="space-y-6">
      <Link to="/">
        <Button variant="ghost" size="sm" className="gap-2">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t('postDetail.backToFeed')}
        </Button>
      </Link>

      {/* Post */}
      <article className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-start gap-3">
          <Link to="/profile/$userId" params={{ userId: post.authorId }} aria-label={authorName}>
            <Avatar className="h-12 w-12">
              <AvatarImage src={post.authorProfileImageUrl ?? undefined} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {initial}
              </AvatarFallback>
            </Avatar>
          </Link>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <Link to="/profile/$userId" params={{ userId: post.authorId }} className="font-medium hover:underline">
                {authorName}
              </Link>
              {post.authorPoints !== null && post.authorPoints !== undefined && (
                <span className="whitespace-nowrap text-xs text-primary">{post.authorPoints} pts</span>
              )}
              {post.isPinned && <Pin className="h-4 w-4 shrink-0 text-primary" role="img" aria-label={t('postDetail.pinned')} />}
              {post.isLocked && <Lock className="h-4 w-4 shrink-0 text-muted-foreground" role="img" aria-label={t('postDetail.locked')} />}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-muted-foreground">
              <Link to={`/spaces/${post.spaceId}`} className="hover:underline">
                {post.spaceName}
              </Link>
              <span>·</span>
              <span className="whitespace-nowrap">{formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })}</span>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <h1 className="text-xl font-bold">{post.title}</h1>
          <RichTextRenderer value={post.content as RichTextDoc} className="mt-3 text-muted-foreground" renderMention={renderMentionLink} />
        </div>

        {post.imageUrl && (
          <img
            src={post.imageUrl}
            alt={post.title}
            className="mt-4 rounded-lg max-h-96 w-full object-cover"
          />
        )}

        {post.type === 'poll' && <Poll postId={postId} />}

        {post.likesCount > 0 && (
          <div className="mt-3">
            <LikersStack postId={postId} likesCount={post.likesCount} />
          </div>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-4">
          <Button
            variant="ghost"
            size="sm"
            className={`gap-1.5 ${isLiked ? 'text-red-500' : ''}`}
            onClick={() => likeMutation.mutate({ targetType: 'post', targetId: postId })}
            aria-pressed={isLiked}
            aria-label={t('postDetail.likeCount', { count: post.likesCount })}
          >
            <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} aria-hidden="true" />
            <span className="whitespace-nowrap">{post.likesCount} {t('post.likes')}</span>
          </Button>
          <span className="flex items-center gap-1.5 whitespace-nowrap text-sm text-muted-foreground" aria-label={t('postDetail.commentCount', { count: post.commentsCount })}>
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            {post.commentsCount} {t('post.comments')}
          </span>
          <ShareMenu
            url={`${window.location.origin}/posts/${postId}`}
            title={post.title}
            text={(() => {
              const plain = extractPlainText(post.content as RichTextDoc);
              return plain.length > 100 ? plain.slice(0, 100) + '...' : plain;
            })()}
          />
        </div>
      </article>

      {/* Comment Form */}
      {!post.isLocked && (
        <form onSubmit={handleSubmitComment} className="rounded-2xl border border-border bg-card p-4" aria-label={t('postDetail.postComment')}>
          <RichTextEditor
            value={commentDoc}
            onChange={setCommentDoc}
            onSubmit={handleSubmitComment}
            searchUsers={searchUsers}
            uploadMedia={uploadMedia}
            placeholder={t('postDetail.commentPlaceholder')}
            hint={t('editor.hint')}
            minHeight={80}
          />
          <div className="mt-3 flex justify-end">
            <Button
              type="submit"
              size="sm"
              disabled={isDocEmpty(commentDoc) || createComment.isPending}
              className="gap-2"
            >
              <Send className="h-4 w-4" aria-hidden="true" />
              {createComment.isPending ? t('postDetail.posting') : t('postDetail.postComment')}
            </Button>
          </div>
        </form>
      )}

      {/* Comments */}
      <section className="space-y-4" aria-labelledby="comments-heading">
        <h2 id="comments-heading" className="font-semibold">{t('postDetail.commentsCount', { count: comments.length })}</h2>
        {comments.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card p-6 text-center">
            <p className="text-muted-foreground">{t('postDetail.noComments')}</p>
          </div>
        ) : (
          comments.map((c) => {
            const cAuthorName = c.authorDisplayName ||
              [c.authorFirstName, c.authorLastName].filter(Boolean).join(' ') ||
              'Anonymous';
            const cInitial = cAuthorName.slice(0, 1).toUpperCase();

            return (
              <div key={c.commentId} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex items-start gap-3">
                  <Link to="/profile/$userId" params={{ userId: c.authorId }} aria-label={cAuthorName}>
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={c.authorProfileImageUrl ?? undefined} />
                      <AvatarFallback className="bg-primary/10 text-sm text-primary">
                        {cInitial}
                      </AvatarFallback>
                    </Avatar>
                  </Link>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                        <Link to="/profile/$userId" params={{ userId: c.authorId }} className="text-sm font-medium hover:underline">
                          {cAuthorName}
                        </Link>
                        <span className="whitespace-nowrap text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 shrink-0 gap-1 px-2 ${commentLikedMap[c.commentId] ? 'text-red-500' : 'text-muted-foreground'}`}
                        aria-pressed={!!commentLikedMap[c.commentId]}
                        aria-label={t('postDetail.likeCommentAria', { count: commentLikeCountOverride[c.commentId] ?? c.likesCount })}
                        onClick={() => commentLikeMutation.mutate({ targetType: 'comment', targetId: c.commentId })}
                      >
                        <Heart className={`h-3.5 w-3.5 ${commentLikedMap[c.commentId] ? 'fill-current' : ''}`} aria-hidden="true" />
                        <span className="text-xs tabular-nums">
                          {commentLikeCountOverride[c.commentId] ?? c.likesCount}
                        </span>
                      </Button>
                    </div>
                    <RichTextRenderer value={c.content as RichTextDoc} className="mt-1 text-sm" renderMention={renderMentionLink} />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </section>
    </div>
  );
}
